/**
 * Modal for editing an existing widget's config (title, metric, group_by,
 * chart_type, series_by, and scope filters).
 *
 * Pre-populates from the widget's current config so editing a preset
 * creates a modified copy, not changing the original preset definition.
 */
import { useState, useEffect } from 'react'
import {
  X, Save, BarChart3, TrendingUp, Grid3x3, Table2, Map as MapIcon, ChevronRight,
  Boxes, Target, Activity, CalendarDays, Workflow, ScatterChart,
} from 'lucide-react'
import { execAPI } from '../../services/exec'
import {
  METRIC_LABELS, DIMENSION_LABELS,
  type MetricKey, type GroupByDimension, type ChartType,
} from './generic/types'
import type { DashboardWidget, WidgetConfig, PortfolioGroup } from '../../types/exec'

interface Props {
  widget: DashboardWidget
  onClose: () => void
  onSave: (updates: Partial<DashboardWidget>) => void
}

const METRIC_OPTIONS: { value: MetricKey; label: string; group: string }[] = [
  { value: 'award_count', label: 'Award Count (actions)', group: 'Volume' },
  { value: 'unique_award_count', label: 'Distinct Contracts', group: 'Volume' },
  { value: 'total_obligated', label: 'Obligated Dollars', group: 'Volume' },
  { value: 'full_open_pct', label: 'Full & Open %', group: 'Competition' },
  { value: 'follow_on_pct', label: 'Follow-On %', group: 'Competition' },
  { value: 'ot_award_pct', label: 'Other Transaction %', group: 'Vehicles' },
  { value: 'deobligation_pct', label: 'Deobligation Rate', group: 'Volume' },
  { value: 'gross_obligated', label: 'Gross Obligated $', group: 'Volume' },
  { value: 'not_competed_pct', label: 'Not Competed %', group: 'Competition' },
  { value: 'single_offer_pct', label: 'Single Offer %', group: 'Competition' },
  { value: 'avg_offers_received', label: 'Avg Offers Received', group: 'Competition' },
  { value: 'sb_award_pct', label: 'SB Award %', group: 'Small Business' },
  { value: 'set_aside_pct', label: 'Set-Aside %', group: 'Small Business' },
  { value: 'count_8a', label: '8(a) Awards', group: 'Small Business' },
  { value: 'count_hubzone', label: 'HUBZone Awards', group: 'Small Business' },
  { value: 'count_sdvosb', label: 'SDVOSB Awards', group: 'Small Business' },
  { value: 'count_wosb', label: 'WOSB Awards', group: 'Small Business' },
]

const GROUP_BY_OPTIONS: { value: GroupByDimension; label: string }[] = [
  { value: 'fiscal_year', label: 'Fiscal Year' },
  { value: 'fiscal_quarter', label: 'Fiscal Quarter' },
  { value: 'sub_agency', label: 'Branch / Sub-Agency' },
  { value: 'macom', label: 'Major Command (MAJCOM)' },
  { value: 'subcom1', label: 'Sub-Command' },
  { value: 'awarding_office_code', label: 'Office' },
  { value: 'vehicle_category', label: 'Vehicle Category' },
  { value: 'psc_level1_code', label: 'PSC Category (Vertical)' },
  { value: 'color_of_money', label: 'Color of Money' },
  { value: 'extent_competed', label: 'Competition Status' },
  { value: 'is_ot', label: 'FAR vs OT' },
]

/**
 * Chart-type options exposed in the "Advanced" override panel. Each preset has
 * a curated chart shape baked in (per Phase A1); this list is the override
 * escape hatch for power users who want a different viz for the same data.
 *
 * Includes the seven Phase C renderers (treemap, bullet, slope, boxplot,
 * calendar, scatter, sankey) — most need specific data shapes to look right
 * (boxplot wants pre-aggregated p25/p50/p75/p90; sankey wants two grouping
 * dims), so the curated presets are usually a better starting point than the
 * raw override.
 */
const CHART_TYPES: { value: ChartType; icon: React.ReactNode; label: string }[] = [
  { value: 'bar', icon: <BarChart3 size={16} />, label: 'Bar' },
  { value: 'line', icon: <TrendingUp size={16} />, label: 'Line' },
  { value: 'heatmap', icon: <Grid3x3 size={16} />, label: 'Heatmap' },
  { value: 'table', icon: <Table2 size={16} />, label: 'Table' },
  { value: 'map', icon: <MapIcon size={16} />, label: 'Map' },
  { value: 'treemap', icon: <Boxes size={16} />, label: 'Treemap' },
  { value: 'bullet', icon: <Target size={16} />, label: 'Bullet' },
  { value: 'slope', icon: <Activity size={16} />, label: 'Slope' },
  { value: 'boxplot', icon: <Activity size={16} />, label: 'Boxplot' },
  { value: 'calendar', icon: <CalendarDays size={16} />, label: 'Calendar' },
  { value: 'scatter', icon: <ScatterChart size={16} />, label: 'Scatter' },
  { value: 'sankey', icon: <Workflow size={16} />, label: 'Sankey' },
]

export default function WidgetConfigEditor({ widget, onClose, onSave }: Props) {
  const wc = widget.config || {}

  const [title, setTitle] = useState(widget.title || '')
  const [metric, setMetric] = useState<MetricKey>(
    ((wc.metrics as MetricKey[] | undefined)?.[0]) || 'award_count',
  )
  const [groupBy, setGroupBy] = useState<GroupByDimension>(
    (wc.group_by as GroupByDimension) || 'fiscal_year',
  )
  const [seriesBy, setSeriesBy] = useState<GroupByDimension | ''>(
    (wc.series_by as GroupByDimension) || '',
  )
  const [chartType, setChartType] = useState<ChartType>(
    (wc.chart_type as ChartType) || 'bar',
  )

  // Scope filter
  const [portfolioGroupId, setPortfolioGroupId] = useState((wc.portfolio_group as string) || '')

  // "Advanced" section is collapsed by default — chart-type override is a
  // power-user escape hatch since each preset has a curated chart shape baked in.
  const [advancedOpen, setAdvancedOpen] = useState(false)

  // Load portfolio groups
  const [portfolioGroups, setPortfolioGroups] = useState<PortfolioGroup[]>([])
  useEffect(() => {
    execAPI.getPortfolioGroups().then(setPortfolioGroups).catch(() => {})
  }, [])

  const handleSave = () => {
    const config: WidgetConfig = {
      group_by: groupBy,
      metrics: [metric],
      chart_type: chartType,
      ...(seriesBy ? { series_by: seriesBy } : {}),
      ...(portfolioGroupId ? { portfolio_group: portfolioGroupId } : {}),
    }

    onSave({
      title: title || undefined,
      config,
    })
  }

  const selectClass = 'w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Edit Widget
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`${METRIC_LABELS[metric]} by ${DIMENSION_LABELS[groupBy]}`}
              className={selectClass}
            />
          </div>

          {/* Metric */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Metric</label>
            <select value={metric} onChange={(e) => setMetric(e.target.value as MetricKey)} className={selectClass}>
              {METRIC_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.group}: {opt.label}</option>
              ))}
            </select>
          </div>

          {/* Group By */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Group By</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupByDimension)} className={selectClass}>
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Compare By */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Compare By</label>
            <select value={seriesBy} onChange={(e) => setSeriesBy(e.target.value as GroupByDimension | '')} className={selectClass}>
              <option value="">None</option>
              {GROUP_BY_OPTIONS.filter((o) => o.value !== groupBy).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Advanced — chart-type override (collapsed by default) */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-2">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wide hover:text-gray-600 dark:hover:text-gray-300"
            >
              <ChevronRight
                size={12}
                className={`transition-transform ${advancedOpen ? 'rotate-90' : ''}`}
              />
              Advanced
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Chart Type
                    <span className="ml-1 text-[10px] font-normal text-gray-400">
                      (preset default — change only if you want a different viz of the same data)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CHART_TYPES.map((ct) => (
                      <button
                        key={ct.value}
                        onClick={() => setChartType(ct.value)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${
                          chartType === ct.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        {ct.icon}
                        {ct.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Scope */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Scope
            </label>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Portfolio Group</label>
              <select value={portfolioGroupId} onChange={(e) => setPortfolioGroupId(e.target.value)} className={selectClass}>
                <option value="">All offices (no scope filter)</option>
                {portfolioGroups.map((pg) => (
                  <option key={pg.id} value={pg.id}>{pg.name} ({pg.resolved_office_count} offices)</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">
                <a href="/exec/portfolio-groups" className="text-blue-500 hover:underline">Manage portfolio groups</a>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Save size={14} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}
